import clsx from 'clsx'

type AmaniState = 'default' | 'thinking' | 'celebrating' | 'listening'

interface AmaniAvatarProps {
  size?: 'sm' | 'md' | 'lg' | 'xl'
  state?: AmaniState
  className?: string
}

/**
 * Amani Avatar Component
 *
 * Per Brand Identity Document (Section 4.2):
 * - Concept: Abstract, Warm, Not Robotic
 * - Primary Avatar: Speech bubble with heart inside
 * - Colors: Bubble = Serenity Teal, Heart = Savannah Clay
 *
 * Why this design:
 * - Speech bubble = conversation (core offering)
 * - Heart = empathy and care (differentiator)
 * - Not a face = avoids uncanny valley, age/gender assumptions
 * - Abstract = timeless, scalable, adaptable
 *
 * States:
 * - default: Primary avatar
 * - thinking: Bubble with "..." dots pulsing
 * - celebrating: Bubble with small stars/sparkles
 * - listening: Bubble with subtle sound wave lines
 */
export function AmaniAvatar({
  size = 'md',
  state = 'default',
  className,
}: AmaniAvatarProps) {
  const sizes = {
    sm: 32,
    md: 48,
    lg: 64,
    xl: 96,
  }

  const dimension = sizes[size]

  return (
    <div
      className={clsx(
        'relative inline-flex items-center justify-center',
        className
      )}
      style={{ width: dimension, height: dimension }}
    >
      <svg
        width={dimension}
        height={dimension}
        viewBox="0 0 48 48"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        {/* Speech bubble background */}
        <path
          d="M6 12C6 8.68629 8.68629 6 12 6H36C39.3137 6 42 8.68629 42 12V28C42 31.3137 39.3137 34 36 34H16L10 40V34H12C8.68629 34 6 31.3137 6 28V12Z"
          fill="#006D77"
        />

        {state === 'default' && <HeartIcon />}
        {state === 'thinking' && <ThinkingDots />}
        {state === 'celebrating' && <CelebratingStars />}
        {state === 'listening' && <ListeningWaves />}
      </svg>
    </div>
  )
}

function HeartIcon() {
  return (
    <path
      d="M24 28C24 28 18 23 18 19C18 16.5 20 15 22 15C23.5 15 24.5 16 24 17C23.5 16 24.5 15 26 15C28 15 30 16.5 30 19C30 23 24 28 24 28Z"
      fill="#E29578"
    />
  )
}

function ThinkingDots() {
  return (
    <g className="animate-pulse-gentle">
      <circle cx="17" cy="20" r="2.5" fill="white" opacity="0.9" />
      <circle cx="24" cy="20" r="2.5" fill="white" opacity="0.7" />
      <circle cx="31" cy="20" r="2.5" fill="white" opacity="0.5" />
    </g>
  )
}

function CelebratingStars() {
  return (
    <>
      <HeartIcon />
      {/* Small sparkles */}
      <circle cx="16" cy="14" r="1.5" fill="#E29578" className="animate-pulse" />
      <circle cx="32" cy="14" r="1" fill="white" className="animate-pulse" />
      <circle cx="34" cy="24" r="1.5" fill="#83C5BE" className="animate-pulse" />
      <circle cx="14" cy="26" r="1" fill="white" className="animate-pulse" />
    </>
  )
}

function ListeningWaves() {
  return (
    <g className="animate-pulse-gentle">
      <rect x="17" y="17" width="2" height="6" rx="1" fill="white" />
      <rect x="21" y="15" width="2" height="10" rx="1" fill="white" />
      <rect x="25" y="17" width="2" height="6" rx="1" fill="white" />
      <rect x="29" y="18" width="2" height="4" rx="1" fill="white" />
    </g>
  )
}

/**
 * Amani greeting component with avatar and message
 */
interface AmaniGreetingProps {
  message?: string
  userName?: string
  className?: string
}

export function AmaniGreeting({
  message,
  userName,
  className,
}: AmaniGreetingProps) {
  const greeting = message || (userName ? `Hello, ${userName}!` : 'Hello!')

  return (
    <div className={clsx('flex items-start gap-3', className)}>
      <AmaniAvatar size="md" state="default" />
      <div className="flex-1 bg-teal-50 rounded-2xl rounded-tl-sm p-4 max-w-md">
        <p className="font-inter text-body text-stone-700">{greeting}</p>
        <p className="font-inter text-body-sm text-stone-500 mt-1">
          I'm Amani, here to help with any questions.
        </p>
      </div>
    </div>
  )
}

/**
 * Compact Amani indicator for sidebars/headers
 */
interface AmaniIndicatorProps {
  status: 'online' | 'busy' | 'offline'
  showLabel?: boolean
  className?: string
}

export function AmaniIndicator({
  status,
  showLabel = true,
  className,
}: AmaniIndicatorProps) {
  const statusColors = {
    online: 'bg-success-500',
    busy: 'bg-warning-500',
    offline: 'bg-stone-400',
  }

  const statusLabels = {
    online: 'Amani is active',
    busy: 'Amani is busy',
    offline: 'Amani is offline',
  }

  return (
    <div className={clsx('flex items-center gap-2', className)}>
      <div className="relative">
        <AmaniAvatar size="sm" state={status === 'busy' ? 'thinking' : 'default'} />
        <span
          className={clsx(
            'absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 border-white',
            statusColors[status]
          )}
        />
      </div>
      {showLabel && (
        <span className="font-inter text-body-sm text-stone-600">
          {statusLabels[status]}
        </span>
      )}
    </div>
  )
}
